/**
 * Обновление списка протягиванием сверху вниз (план 110).
 *
 * ЗАЧЕМ СВОЁ. В приложении, запущенном с домашнего экрана, нативного
 * pull-to-refresh нет вовсе: браузерной обвязки с её жестом там просто не
 * существует, а Safari внутри standalone тянуть страницу не даёт. Человек
 * тянет экран, ничего не происходит — и он не понимает, свежие ли перед ним
 * данные (жалоба владельца 28.08).
 *
 * ЧТО ОБНОВЛЯЕТСЯ. Все активные запросы TanStack Query — то же самое, что
 * происходит при возвращении на вкладку. Отдельного «обнови вот этот список»
 * не делаем: человек тянет экран целиком и ждёт, что обновится всё видимое.
 *
 * ЖЕСТ. Реагируем только когда страница уже в самом верху и палец идёт вниз:
 * иначе жест перехватывал бы обычную прокрутку. Порог в 70px — чтобы случайное
 * движение при чтении не дёргало сеть.
 */
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useIsMobile } from '@/lib/use-media';
import { cn } from '@/lib/utils';

/** Насколько нужно протянуть, чтобы обновление сработало. */
const THRESHOLD = 70;
/** Дальше этого индикатор не едет — тянуть бесконечно незачем. */
const MAX_PULL = 110;

export function PullToRefresh(): JSX.Element | null {
  const isMobile = useIsMobile();
  const qc = useQueryClient();
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await qc.refetchQueries({ type: 'active' });
    } finally {
      // Небольшая задержка намеренно: мгновенно исчезнувший индикатор читается
      // как «ничего не произошло», и человек тянет снова.
      setTimeout(() => {
        setRefreshing(false);
        setPull(0);
      }, 300);
    }
  }, [qc]);

  useEffect(() => {
    if (!isMobile) return undefined;

    const onStart = (e: TouchEvent): void => {
      // Только от самого верха страницы: иначе жест конфликтует с прокруткой.
      if (window.scrollY > 0 || refreshing) {
        startY.current = null;
        return;
      }
      startY.current = e.touches[0]?.clientY ?? null;
    };

    const onMove = (e: TouchEvent): void => {
      if (startY.current === null || refreshing) return;
      const y = e.touches[0]?.clientY ?? 0;
      const delta = y - startY.current;
      if (delta <= 0) {
        setPull(0);
        return;
      }
      // Сопротивление: экран идёт за пальцем медленнее, как в нативных списках.
      setPull(Math.min(MAX_PULL, delta * 0.5));
    };

    const onEnd = (): void => {
      if (startY.current === null) return;
      startY.current = null;
      setPull((current) => {
        if (current >= THRESHOLD) void refresh();
        else return 0;
        return current;
      });
    };

    // passive: жест не отменяем — страница продолжает вести себя обычно, мы
    // только рисуем индикатор поверх.
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    window.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [isMobile, refreshing, refresh]);

  if (!isMobile) return null;
  const visible = pull > 0 || refreshing;
  if (!visible) return null;

  const ready = pull >= THRESHOLD;
  const offset = refreshing ? THRESHOLD : pull;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-40 flex justify-center"
      style={{ paddingTop: `calc(${offset}px + env(safe-area-inset-top))` }}
      aria-live="polite"
    >
      <div
        className={cn(
          'flex items-center gap-2 rounded-full bg-ink-black/85 px-4 py-2 text-xs font-medium text-canvas-white shadow-lg',
        )}
        data-testid="pull-to-refresh"
      >
        <RefreshCw
          className={cn('h-4 w-4', refreshing && 'animate-spin')}
          style={refreshing ? undefined : { transform: `rotate(${pull * 3}deg)` }}
        />
        {refreshing ? 'Обновляем…' : ready ? 'Отпустите — обновим' : 'Потяните вниз'}
      </div>
    </div>
  );
}
